/**
 * 루트 컴포넌트 — 라우팅 진입점
 *
 *  - /login, /login/totp 는 인증 가드 X
 *  - 그 외 모든 라우트는 ProtectedRoute (미로그인 시 /login 으로)
 *
 * 본 개발 시 placeholder 페이지를 mockups/index.html 의 9개 라우트(dashboard /
 * sales-list / stock / purchase-list / analysis / price-list / g2b / system / todo)로
 * 교체.
 *
 * 참고: docs/api_spec.md (호출할 API 계약), mockups/index.html (UI 패턴)
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './lib/auth-context';
import { getPendingCounts } from './lib/approval-api';
import { MENU_GROUPS, visibleItems, isItemActive, pageTitle, stripEmoji, type MenuItem } from './lib/menu';
import { HIDDEN_GROUP_KEY, readCachedNavLayout, resolveNavLayout, writeCachedNavLayout } from './lib/nav-layout';
import { groupView, readGroupOpenState, toggleGroupState, writeGroupOpenState, type GroupOpenState } from './lib/nav-groups-open';
import { getPreferences } from './lib/preferences-api';
import type { NavLayout } from './lib/types';
import { MenuIcon } from './components/ui/MenuIcon';
import { AutoSyncTicker } from './components/AutoSyncTicker';
import { IdleLogout } from './components/IdleLogout';
import { CellTooltipMount } from './components/CellTooltipMount';
import { CellSumSelectionMount } from './components/CellSumSelectionMount';
import { PdfPreviewModal } from './components/PdfPreviewModal';
import { MailComposeModal } from './components/MailComposeModal';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MobileAppTabs } from './components/MobileAppTabs';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/Login';
import { LoginTotpPage } from './pages/LoginTotp';
import { PasswordResetRequestPage } from './pages/PasswordResetRequest';
import { PasswordResetConfirmPage } from './pages/PasswordResetConfirm';
import { SalesListPage } from './pages/SalesList';
import { PurchaseListPage } from './pages/PurchaseList';
import { StockListPage } from './pages/StockList';
import { AnalysisPage } from './pages/Analysis';
import { MyPage } from './pages/MyPage';
import { SystemPage } from './pages/SystemPage';
import { StaffDirectoryPage } from './pages/StaffDirectoryPage';
// 개발 계획 (v2.180.0) — admin 뿐. 화면도 서버도 막는다
import { DevRequestPage } from './pages/DevRequestPage';
import { CornerActions } from './components/DevRequestFab';
import { PartnerCompanyPage } from './pages/PartnerCompanyPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { PriceListPage } from './pages/PriceList';
// 행사 부스 QR 설문 집계 (v2.159.0) — 개인 연락처가 들어 있어 viewer 차단
import { MarketingPage } from './pages/MarketingPage';
import { G2BPage } from './pages/G2B';
// 인포트렌드 사이징 툴 (v2.233.0) — 서버 없는 계산기 화면. 전 직원
import { SizingToolPage } from './pages/SizingToolPage';
import { DashboardPage } from './pages/Dashboard';
import { DocumentsPage } from './pages/Documents';
import { ExpensesPage } from './pages/Expenses';
import { MobileInventory } from './pages/MobileInventory';
import { MobileStockCheck } from './pages/MobileStockCheck';
import { MobileStockAuditList, MobileStockAuditRun } from './pages/MobileStockAudit';
import { MobileGiftList } from './pages/MobileGiftList';
import { ApprovalInboxPage } from './pages/ApprovalInbox';
import { MaintenancePage } from './pages/MaintenancePage';
import { EmergencyBellPage } from './pages/EmergencyBellPage';
import { ImportExportPage } from './pages/ImportExportPage';
import { NasExplorerPage } from './pages/NasExplorerPage';
import { MailPage } from './pages/MailPage';
import { CalendarPage } from './pages/CalendarPage';
import { TasksPage } from './pages/TasksPage';
import { TopBar } from './components/TopBar';
import { DealRegistrationPage } from './pages/DealRegistrationPage';
// 💬 웍스 「포털챗봇」 처음 한 번 연결 (v2.282.0) — 봇이 1:1 로 보낸 링크(#t=…)로만 들어온다. 전 직원
import { HelpbotLinkPage } from './pages/HelpbotLinkPage';
// 🧭 메뉴 배치 편집 창 (v2.320.0) — 끌어다 놓기 라이브러리(dnd-kit)가 들어 있어 누를 때만 받는다
const MenuLayoutEditor = lazy(() => import('./components/MenuLayoutEditor').then((m) => ({ default: m.MenuLayoutEditor })));

// 메뉴 정의는 lib/menu.ts — 그룹 4개짜리 **한 벌** + 공용 하단(기술지원포털 통로·마이페이지·로그아웃).
//   v2.135.0 (2026-09-08 사용자 확정): 세 구역(v2.127.0)을 되돌렸다. 「메뉴가 여기저기 중복이라 정작
//   필요한 메뉴를 찾기 어렵다」가 이유. 주소는 한 개도 안 없앴다 — /dashboard·/home 은 / 로 넘긴다.

export function App() {
  return (
    <>
      <TitleSync />
      <Routes>
      <Route path="/login" element={<RedirectIfAuthed><LoginPage /></RedirectIfAuthed>} />
      <Route path="/login/totp" element={<RedirectIfAuthed><LoginTotpPage /></RedirectIfAuthed>} />
      <Route path="/password-reset" element={<PasswordResetRequestPage />} />
      <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
      {/* 모바일 PWA 전용 라우트 — 사이드바 없는 standalone 화면 */}
      <Route
        path="/m/*"
        element={
          <ProtectedRoute>
            <MobileShell />
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Shell />
          </ProtectedRoute>
        }
      />
      </Routes>
    </>
  );
}

/**
 * 브라우저 탭 제목 — 「내 결재함 · SM 포털」 (v2.128.0).
 * 전에는 모든 화면이 「SM 포털」 한 가지라 탭이 여럿이면 어느 것이 결재함인지 몰랐고, 방문 기록·즐겨찾기에도
 * 화면 이름이 안 남았다. 이름은 메뉴 정의(lib/menu.ts)에서 가져온다 — 메뉴와 탭 제목이 어긋나지 않게.
 */
function TitleSync() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    document.title = pageTitle(pathname, search);
  }, [pathname, search]);
  return null;
}

/**
 * 모바일 PWA 전용 쉘 — 사이드바/햄버거 없음, AutoSync/Tooltip 만 유지.
 *
 * ⭐**홈 화면 앱은 「업무 화면만」 하는 껍데기다.** 사이드바가 없으니 다른 상위 메뉴로
 *   넘어갈 길이 없다 — 앱을 깐 사람이 업무 화면만 보게 하려는 것이다.
 *   · `/m/inventory` → 📦 재고 (v2.0)
 *   · `/m/calendar`  → 📅 일정 (v2.185.0 · 사용자 요청 「일정 앱에서는 다른 상위 메뉴로
 *      못넘어가게 해줘. 기존 SM재고 앱이 … 재고관리메뉴만 쓸수 있는 형태로. 그런것처럼」)
 *   그 밖의 경로는 inventory 로 보낸다.
 *
 * ⭐⭐⭐**앱은 「하나」다 — 두 화면은 앱 안에서 두 칸으로 바꾼다** (v2.188.0 · 2026-09-17).
 *   한때 「SM 일정」을 **두 번째 앱**으로 깔게 했지만, 안드로이드는 **한 주소에 앱 하나**만 깔게 해서
 *   (이미 깔린 앱의 범위가 이 주소를 덮으면 설치 신호를 안 던진다) 영영 안 깔렸다.
 *   사정과 대안 비교는 `components/MobileAppTabs.tsx` 머리글에.
 *
 * ⚠**일정 화면은 스스로 높이를 재지 않는다**(사이드바 있는 쪽에서는 바깥이 스크롤을 맡았다).
 *   이 껍데기는 `h-screen` 이라 그대로 넣으면 아래가 잘린다 → 스크롤 상자로 감싼다.
 */
function MobileShell() {
  const { pathname } = useLocation();
  // mobile-shell — 본문 글자(text-sm)를 폰에서는 14px 로 되돌린다(index.css · v2.317.1 PC 는 13px)
  return (
    <div className="mobile-shell h-screen bg-slate-50 text-slate-900">
      <AutoSyncTicker />
      {/* 8시간 안 쓰면 로그아웃 (v2.231.0 · F-S7) */}
      <IdleLogout />
      <CellTooltipMount />
      <PdfPreviewModal />
      {/* PdfPreviewModal 의 ✉ 버튼이 여는 전역 모달 — 호스트가 없으면 클릭이 무반응이 된다 */}
      <MailComposeModal />
      <ErrorBoundary resetKey={pathname}>
        <Routes>
          <Route path="inventory" element={<MobileInventory />} />
          {/* 📦 주요재고 「하나씩 확인」 (v2.198.0) — 매주 월요일 메신저 링크가 여기로 온다.
              ⛔주소에 물음표를 붙이지 않는다(「이번 주」는 서버가 정한다) — 로그인 뒤 돌아올 때
              물음표 뒤가 버려지던 자리를 함께 고쳤지만, 이미 나간 링크는 계속 돌아다닌다. */}
          <Route path="stock-check" element={<MobileStockCheck />} />
          {/* 🗓 분기 재고 실사 (v2.224.0) — 분기 고르기 · 한 품목씩 확인(사진은 NAS) */}
          <Route path="stock-audit" element={<MobileStockAuditList />} />
          <Route path="stock-audit/:id" element={<MobileStockAuditRun />} />
          {/* 🎁 명절선물 「내 전달 목록」 (v2.303.0) — D-45·D-14 포털챗봇 알림 링크 · 명절선물 탭 [📱 폰 목록] 이 여기로 온다(두 칸 전환 줄엔 안 넣는다 — 명절 무렵에만 쓴다) */}
          <Route path="gifts" element={<MobileGiftList />} />
          <Route
            path="calendar"
            element={
              /* ⭐두 칸 전환 줄을 **여기** 얹는다 (v2.188.0). 일정 화면 안에 넣으면
                 「연동 안 된 서버」 같은 다른 갈래에서는 줄이 사라진다 — 껍데기가 맡으면 늘 있다.
                 재고 화면은 반대로 **자기 헤더 안**에 넣었다(그 화면은 페이지 전체가 스크롤된다).
                 ⚠`flex-1 min-h-0` 없이 두면 스크롤 상자가 줄 높이만큼 삐져나가 아래가 잘린다. */
              <div className="h-full flex flex-col">
                <div className="bg-slate-900 px-3 py-2 shadow flex items-center">
                  <MobileAppTabs />
                </div>
                <div
                  className="flex-1 min-h-0 overflow-y-auto"
                  style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                >
                  <CalendarPage />
                </div>
              </div>
            }
          />
          <Route path="*" element={<Navigate to="/m/inventory" replace />} />
        </Routes>
      </ErrorBoundary>
    </div>
  );
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * viewer 권한일 때 페이지 진입 차단. URL 직접 입력 시도까지 막음.
 * 사용자 정책: 매출 분석 / 주요항목 가격표 메뉴는 viewer 한테 완전히 가림.
 */
function ViewerBlocked({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role === 'viewer') return <Navigate to="/" replace />;
  return <>{children}</>;
}

const SIDEBAR_COLLAPSED_KEY = 'sm-sidebar-collapsed';

function Shell() {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // 데스크탑 사이드바 접힘 상태 — localStorage 영구화 (페이지 이동/새로고침 시 유지)
  const [desktopCollapsed, setDesktopCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, desktopCollapsed ? '1' : '0');
    } catch {
      /* localStorage 비활성 환경 무시 */
    }
  }, [desktopCollapsed]);
  const closeMobile = () => setMobileOpen(false);
  const toggleDesktop = () => setDesktopCollapsed((v) => !v);
  return (
    <div className="flex h-screen bg-slate-50 text-slate-900">
      <AutoSyncTicker />
      {/* 8시간 안 쓰면 로그아웃 (v2.231.0 · F-S7) */}
      <IdleLogout />
      <CellTooltipMount />
      <CellSumSelectionMount />
      <PdfPreviewModal />
      <MailComposeModal />
      {/* 오른쪽 위 [💬 사용 문의] [📮 개발요청] 단추 — 어느 메뉴에서든 (v2.274.0 개발요청 · v2.275.0 사용 문의) */}
      <CornerActions />
      {/* 모바일 햄버거 버튼 — md 이상 숨김 */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="메뉴 열기"
        className="md:hidden fixed top-2 left-2 z-30 bg-slate-900 text-white p-2 rounded shadow-md hover:bg-slate-800"
      >
        <span className="block w-5 h-0.5 bg-current mb-1" />
        <span className="block w-5 h-0.5 bg-current mb-1" />
        <span className="block w-5 h-0.5 bg-current" />
      </button>
      {/* 데스크탑 사이드바 토글 — md 이상에서만. 화면 세로 중앙, 사이드바 우측 가장자리 핸들. */}
      <button
        type="button"
        onClick={toggleDesktop}
        aria-label={desktopCollapsed ? '메뉴 펼치기' : '메뉴 접기'}
        title={desktopCollapsed ? '메뉴 펼치기' : '메뉴 접기'}
        className={[
          'hidden md:flex fixed top-1/2 -translate-y-1/2 z-50',
          'w-7 h-20 items-center justify-center',
          'rounded-r bg-slate-800 text-white shadow-lg',
          'hover:bg-blue-600 hover:w-8 transition-all duration-200',
          'border-y border-r border-slate-700',
          desktopCollapsed ? 'left-0' : 'left-60',
        ].join(' ')}
      >
        <span className="text-xl leading-none font-bold">
          {desktopCollapsed ? '›' : '‹'}
        </span>
      </button>
      {/* 모바일 백드롭 — 사이드바 열림 시 */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={closeMobile}
        desktopCollapsed={desktopCollapsed}
      />
      <main className="flex-1 overflow-y-auto">
        {/* v2.319.0 상단 헤더 한 줄(PC) — 전역 검색 · 최근 · ★ · 🔔 · [사용 문의][개발요청]. 폰은 종전 오른쪽 아래 단추(CornerActions) */}
        <TopBar />
        <ErrorBoundary resetKey={pathname}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            {/* 구역 시절 주소(v2.127.0~v2.134.0) — 즐겨찾기·옛 링크가 깨지지 않게 대시보드로 넘긴다.
                `/home` 은 구역을 고르던 타일 홈이었다(v2.135.0 에서 화면 자체를 없앴다). */}
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/sales" element={<SalesListPage />} />
            {/* v2.140.0: 벤더 딜등록 목록 — viewer 는 화면도 서버도 막는다 */}
            <Route path="/deal-registration" element={<ViewerBlocked><DealRegistrationPage /></ViewerBlocked>} />
            {/* v2.251.0: 유지보수는 조회 전용(viewer)도 본다 — 금액은 서버가 null 로 가려 보낸다(화면 「***」) */}
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/bell" element={<ViewerBlocked><EmergencyBellPage /></ViewerBlocked>} />
            <Route path="/stock" element={<StockListPage />} />
            <Route path="/purchases" element={<PurchaseListPage />} />
            <Route path="/analysis" element={<ViewerBlocked><AnalysisPage /></ViewerBlocked>} />
            <Route path="/price-list" element={<ViewerBlocked><PriceListPage /></ViewerBlocked>} />
            {/* v2.245.0: 📣 마케팅 = 🎁 명절선물 + 📋 부스 설문 집계(탭) — viewer 는 화면도 서버도 막는다.
                옛 주소 /survey-results 는 설문 탭으로 넘긴다(부스에서 쓰던 즐겨찾기·QR 안내가 깨지지 않게). */}
            <Route path="/marketing" element={<ViewerBlocked><MarketingPage /></ViewerBlocked>} />
            <Route path="/survey-results" element={<Navigate to="/marketing?tab=survey" replace />} />
            <Route path="/g2b" element={<G2BPage />} />
            <Route path="/sizing-tool" element={<SizingToolPage />} />
            {/* v2.50.0: 지출결재는 전 직원 사용 — 라우트 가드 해제, 화면 안에서 viewer 탭 제한 */}
            <Route path="/documents" element={<DocumentsPage />} />
            {/* v2.53.0: 지출·경비 전용 메뉴 — 전 직원. 내용은 서버가 행 단위로 거른다 */}
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/approvals" element={<ApprovalInboxPage />} />
            {/* v2.143.0: 거래처 담당자 주소록 탭이 생기며 sales 에게도 열렸다 — viewer 만 막는다(개인 연락처) */}
            {/* v2.197.0: viewer 도 **읽기 전용**으로 들어온다(사용자 지시) — 쓰기 단추는 화면에서 감추고
                서버가 sales 이상으로 막는다. 보이는 탭은 담당자 주소록 하나뿐. */}
            <Route path="/partners" element={<PartnerCompanyPage />} />
            <Route path="/imports" element={<ImportExportPage />} />
            <Route path="/system" element={<SystemPage />} />
            {/* 👥 직원 명부 (v2.325.0) — 관리자 + 경영지원 지정 · 화면 안에서 막고 서버(/admin/staff)도 막는다 */}
            <Route path="/staff" element={<StaffDirectoryPage />} />
            {/* v2.274.0: 옛 「개발 계획」(v2.180.0 admin 전용) 자리 = 📮 개발요청 게시판 — 전 직원(조회 전용 포함).
                주소는 그대로 둔다. 서버 가드는 /dev-requests 가 맡는다(옛 /dev-plan 통로는 admin 전용 그대로 · 화면에서 안 쓴다). */}
            <Route path="/dev-plan" element={<DevRequestPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            {/* v2.114.0: NAS 파일탐색기 — 전 직원. 보이는 범위는 각자 연결한 NAS 계정 권한이 정한다
                (포털이 권한을 두 벌로 관리하지 않는다). 연결 전에는 화면이 안내만 띄운다. */}
            {/* v2.205.0: 받은 메일 보기·쓰기 — v2.250.0 부터 전 직원(viewer 포함 · 각자 연결한 본인 메일함만 보인다) */}
            <Route path="/mail" element={<MailPage />} />
            <Route path="/nas-files" element={<NasExplorerPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            {/* v2.319.0: ✅ 할 일 — viewer 는 메뉴(hideForViewer)와 화면(ViewerBlocked) 둘 다 막는다(check:menu ⑥) */}
            <Route path="/tasks" element={<ViewerBlocked><TasksPage /></ViewerBlocked>} />
            <Route path="/mypage" element={<MyPage />} />
            <Route path="/helpbot-link" element={<HelpbotLinkPage />} />
            <Route path="*" element={<Placeholder name="찾을 수 없는 화면" />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function Sidebar({
  mobileOpen,
  onMobileClose,
  desktopCollapsed,
}: {
  mobileOpen: boolean;
  onMobileClose: () => void;
  desktopCollapsed: boolean;
}) {
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  // 묶음 접기·펼치기 — 사람이 펼친 것·접은 것을 이 PC 브라우저에 (v2.318.0 도구·관리 → v2.327.0 모든 묶음 · 개발요청 #40 · lib/nav-groups-open.ts)
  const [groupState, setGroupState] = useState<GroupOpenState>(readGroupOpenState);
  const toggleGroup = (heading: string, expandedNow: boolean): void => {
    setGroupState((prev) => {
      const next = toggleGroupState(heading, expandedNow, prev);
      writeGroupOpenState(next);
      return next;
    });
  };
  const viewOf = (heading: string, defaultClosed: boolean, items: MenuItem[]) =>
    groupView(heading, defaultClosed, items, groupState, (r) => isItemActive(r, pathname));
  // 내 결재함 배지 — 내가 처리할 결재(승인 대기·반려) 건수. 5분 주기 폴링.
  //   v2.50.0: viewer 포함 — 지출결재는 전 직원이 쓰고, viewer 몫 판보 수는 서버가 0 으로 준다.
  //   서버는 합계·판보 몫·사내결재 몫 셋을 주지만(getPendingCounts) 메뉴가 한 벌이라 **합계**만 단다.
  const pendingQuery = useQuery({
    queryKey: ['approvals', 'pendingCount'],
    queryFn: getPendingCounts,
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const pendingCount = pendingQuery.data?.count ?? 0;

  // 🧭 내 메뉴 배치 (v2.320.0 · lib/nav-layout.ts) — 서버 preferences.navLayout(사람마다). 없으면 기본 8묶음.
  //   서버 응답 전에는 이 브라우저에 적어 둔 마지막 배치로 먼저 그려 깜빡임을 막는다(정답은 서버 값 · 오면 그 값으로 다시 적는다).
  const prefsQuery = useQuery({ queryKey: ['preferences'], queryFn: getPreferences, enabled: !!user, staleTime: 5 * 60_000 });
  const userId = user?.userId ?? '';
  const serverLayout: NavLayout | null | undefined = prefsQuery.data ? (prefsQuery.data.navLayout ?? null) : undefined;
  const layout = serverLayout !== undefined ? serverLayout : (userId ? readCachedNavLayout(userId) : null);
  const serverLayoutJson = serverLayout === undefined ? null : JSON.stringify(serverLayout);
  useEffect(() => {
    if (serverLayoutJson === null || !userId) return;
    writeCachedNavLayout(userId, JSON.parse(serverLayoutJson) as NavLayout | null);
  }, [serverLayoutJson, userId]);
  const nav = resolveNavLayout(user, layout);
  const hiddenView = viewOf(HIDDEN_GROUP_KEY, true, nav.hidden);
  const [editorOpen, setEditorOpen] = useState(false);

  // 메뉴 한 줄 — 기본 8묶음과 내 배치가 같은 모양으로 그린다
  const renderItem = (r: MenuItem) => {
    // 이모지는 고정 폭 칸에 — 글자 시작 줄이 메뉴마다 어긋나지 않게(✉·🗂 처럼 좁은 그림문자 · v2.257.0).
    // 켜진 메뉴의 파란 줄은 꺼진 메뉴에도 투명 줄로 자리를 잡아 두어 글자가 밀리지 않는다.
    // 아이콘과 글자 사이 = gap-2.5 (v2.324.1 · 개발요청 #36) — 전에는 칸 사이 띄어쓰기가 flex 에서 사라져 2px 뿐이었다.
    const text = stripEmoji(r.label);
    const icon = r.label.slice(0, r.label.length - text.length).trim();
    return (
      <Link
        key={r.path}
        to={r.path}
        onClick={onMobileClose}
        className={`flex items-center gap-2.5 border-l-4 px-4 py-2.5 text-sm max-md:text-base hover:bg-slate-800 ${
          isItemActive(r, pathname) ? 'border-blue-500 bg-slate-800' : 'border-transparent'
        }`}
      >
        <span className="inline-flex w-6 shrink-0 items-center justify-center" aria-hidden="true">
          {/* v2.317.0 · 메뉴 아이콘 한 벌(선 그림 + 종류 색) — label 의 그림문자는 다른 소비자(챗봇·개발요청·탭 제목)를 위해 남기고
              사이드바는 icon 키가 있으면 그것을 그린다. 그림 아이콘(iconSrc · 인포트렌드 잎·나라장터 문장)은 종전대로. */}
          {r.icon ? (
            <MenuIcon name={r.icon} size={20} />
          ) : r.iconSrc ? (
            <img src={r.iconSrc} alt="" className="block mx-auto w-4 h-4 max-md:w-[18px] max-md:h-[18px]" />
          ) : (
            icon
          )}
        </span>
        <span className="min-w-0 truncate">{text}</span>
        {r.badge && pendingCount > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold align-middle"
            title="내가 처리할 결재 (승인 대기 · 반려)"
          >
            {pendingCount > 99 ? '99+' : pendingCount}
          </span>
        )}
      </Link>
    );
  };

  // 모바일: fixed + transform 슬라이드
  // 데스크탑(md+): 펼침=w-60 static / 접힘=w-0 + 화면 밖으로 슬라이드 (main 영역이 전체 너비 차지)
  const asideClass = [
    'bg-slate-900 text-slate-100 flex flex-col flex-shrink-0',
    'fixed inset-y-0 left-0 z-50 transition-all duration-200 overflow-hidden',
    // 모바일 폭
    'w-60',
    // 모바일 표시 토글
    mobileOpen ? 'translate-x-0' : '-translate-x-full',
    // 데스크탑(md+) — 펼침/접힘 분기
    desktopCollapsed
      ? 'md:w-0 md:-translate-x-full md:z-auto'
      : 'md:w-60 md:static md:translate-x-0 md:z-auto',
  ].join(' ');
  return (
    <>
    <aside className={asideClass}>
      <div className="px-4 pt-5 pb-3 border-b border-slate-800">
        <div className="mb-3">
          <img src="/logo_white.png" alt="SM SYSTEMS" className="w-full h-auto block" />
        </div>
        {/* 이름 = SM 포털(영업만 쓰는 도구가 아니다). 누르면 대시보드로 — 로그인 직후와 같은 자리. */}
        <Link
          to="/"
          onClick={onMobileClose}
          title="대시보드로"
          className={`block text-center rounded py-1 hover:bg-slate-800 ${pathname === '/' ? 'bg-slate-800' : ''}`}
        >
          <div className="font-bold text-sm">SM 포털</div>
          <div className="text-xs text-slate-500">v.{__APP_VERSION__}</div>
        </Link>
      </div>
      <nav className="flex-1 py-2 overflow-y-auto">
        {nav.custom ? (
          <>
            {/* 🧭 내 배치 (v2.320.0 · lib/nav-layout.ts) — 보이는 메뉴를 묶음 머리글 없이 내 순서대로 · 숨긴 메뉴는 맨 아래 한 묶음(접힘 · 켜진 메뉴가 안에 있으면 펼침) */}
            {nav.shown.map(renderItem)}
            {nav.hidden.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => toggleGroup(HIDDEN_GROUP_KEY, hiddenView.expanded)}
                  aria-expanded={hiddenView.expanded}
                  className="w-full flex items-center justify-between px-5 pt-3 pb-1 text-xs font-semibold tracking-wider text-slate-500 hover:text-slate-300 select-none"
                  title={hiddenView.expanded ? '접기' : `펼치기 — ${nav.hidden.map((r) => stripEmoji(r.label)).join(' · ')}`}
                >
                  <span>숨긴 메뉴 {nav.hidden.length}</span>
                  <span aria-hidden="true" className="text-[11px]">{hiddenView.expanded ? '▲' : '▼'}</span>
                </button>
                {hiddenView.items.map(renderItem)}
              </div>
            )}
          </>
        ) : (
          MENU_GROUPS.map((g) => {
            const items = visibleItems(g.items, user);
            if (items.length === 0) return null;
            // 모든 묶음이 머리글로 접고 펼친다(v2.327.0 · #40) — 처음 모습은 collapsible(도구·관리)만 접힘 · 그대로
            const v = viewOf(g.heading, !!g.collapsible, items);
            return (
              <div key={g.heading}>
                {/* 묶음 이름 — 11px(10px 는 어두운 바탕에서 너무 작았다 · v2.257.0) */}
                <button
                  type="button"
                  onClick={() => toggleGroup(g.heading, v.expanded)}
                  aria-expanded={v.expanded}
                  className="w-full flex items-center justify-between px-5 pt-3 pb-1 text-xs font-semibold tracking-wider text-slate-500 hover:text-slate-300 select-none"
                  title={v.expanded ? '접기' : `펼치기 — ${items.map((r) => stripEmoji(r.label)).join(' · ')}`}
                >
                  <span>{g.heading}</span>
                  <span aria-hidden="true" className="text-[11px]">{v.expanded ? '▲' : '▼'}</span>
                </button>
                {v.items.map(renderItem)}
              </div>
            );
          })
        )}
        {/* ✎ 메뉴 배치 바꾸기 (v2.320.0) — 끌어다 놓기 편집 창(components/MenuLayoutEditor). 기본 배치든 내 배치든 늘 맨 아래 한 줄 */}
        <button
          type="button"
          onClick={() => setEditorOpen(true)}
          className="w-full flex items-center gap-2 px-5 pt-4 pb-2 text-xs text-slate-500 hover:text-slate-300 select-none"
          title="왼쪽 메뉴를 내 식으로 — 쓰는 메뉴만 자주 쓰는 순서대로, 안 쓰는 메뉴는 숨김으로 (끌어다 놓기)"
        >
          <span aria-hidden="true">✎</span>
          <span>메뉴 배치 바꾸기</span>
        </button>
      </nav>
      {/* 맨 아래 자리 — 기술지원포털 통로 · 마이페이지 · 로그아웃.
          기술지원포털은 **별도 시스템**(별도 로그인)이라 메뉴 그룹에 섞지 않고 여기 한 줄로 둔다.
          대시보드에도 같은 통로가 버튼으로 있다 — 다른 화면에 있을 때 대시보드를 거치지 않게. */}
      <div className="px-5 py-3 border-t border-slate-800 text-xs space-y-0.5">
        {user?.tspPortalUrl && (
          <a
            href={user.tspPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onMobileClose}
            title="기술지원포털 — 별도 시스템 · 별도 로그인 (새 창)"
            className="block -mx-2 px-2 py-1.5 rounded hover:bg-slate-800 text-slate-300"
          >
            🛠 기술지원포털 ↗
          </a>
        )}
        <Link
          to="/mypage"
          title="마이페이지로 이동"
          onClick={onMobileClose}
          className={`block -mx-2 px-2 py-1.5 rounded hover:bg-slate-800 ${
            pathname === '/mypage' ? 'bg-slate-800 ring-1 ring-blue-500' : ''
          }`}
        >
          <div className="text-slate-300">
            {user?.name ?? user?.userId ?? '—'}
          </div>
          <div className="text-slate-500">
            {user?.role} · 보안 {user?.securityLevel}
          </div>
          <div className="text-xs text-blue-400 mt-1">
            ⚙ 마이페이지 →
          </div>
        </Link>
        <button
          type="button"
          onClick={() => { void signOut(); }}
          className="btn w-full mt-1 bg-slate-700 text-slate-100 hover:bg-slate-600"
        >
          로그아웃
        </button>
      </div>
    </aside>
    {/* 🧭 메뉴 배치 편집 창 — aside(transform) 바깥에 띄운다 */}
    {editorOpen && (
      <Suspense fallback={null}>
        <MenuLayoutEditor onClose={() => setEditorOpen(false)} />
      </Suspense>
    )}
    </>
  );
}

/** 없는 주소 — 옛 링크·오타. 「개발 중」 문구 대신 돌아갈 길을 준다 (v2.128.0). */
function Placeholder({ name }: { name: string }) {
  const { pathname } = useLocation();
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center max-w-md px-6">
        <div className="text-6xl mb-3">🔍</div>
        <h1 className="text-2xl font-bold">{name}</h1>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">
          <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{pathname}</code> 주소에는 화면이 없습니다.
          <br />
          주소가 바뀌었거나 잘못 적힌 링크입니다. 왼쪽 메뉴에서 찾아 주세요.
        </p>
        <Link
          to="/"
          className="inline-block mt-5 px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
        >
          대시보드로
        </Link>
      </div>
    </div>
  );
}
